import './index.css'
import mixpanel from '@disco/api/mixpanel'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { Box, Dialog, Flex, Theme } from 'components/ds'
import React, { Component } from 'react'
import { DropTarget } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import {
  canFormatBeOptimised,
  getExtension,
  getFilesFromDraggedItem,
  getFileTypeByExt,
} from '@disco/utils/file-utils'
import FileSelect from 'components/common/file-select'
import PendingStatus from 'components/explorer/track/track-media/status'
import Button from 'components/ui/button'

function handleUploadingFiles(props, files) {
  const [file] = files
  const ext = getExtension(file.name)

  if (props.isAdding) {
    if (
      canFormatBeOptimised(ext) === canFormatBeOptimised(props.existingFormat)
    )
      return

    if (canFormatBeOptimised(ext)) {
      mixpanel.sendPureEvent('Add lossless version', {
        Format: ext,
      })
    }
  } else {
    if (canFormatBeOptimised(ext) !== canFormatBeOptimised(props.format)) return
    const event = canFormatBeOptimised(ext)
      ? 'Replace lossless version'
      : 'Replace optimised version'
    mixpanel.sendPureEvent(event, {
      Format: ext,
    })
  }

  const abortController = props.uploadTrackFileToS3(
    file,
    props.track.upload_policy_v2_url,
  )
  props.onUpload(abortController)
}

const uploadTrackFileTarget = {
  drop(props, monitor) {
    const draggedItem = monitor.getItem()
    // don't handle drop if child already handle it
    if (monitor.didDrop()) return

    if (draggedItem.items) {
      getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
        if (files.length > 0) {
          handleUploadingFiles(props, files)
        }
      })
    }
  },
}

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({
      shallow: false,
    }),
  }
}

type Props = {
  track: Record<string, any>
  onClose: (...args: Array<any>) => any
  onDone: (...args: Array<any>) => any
  onUpload: (...args: Array<any>) => any
  existingFormat: string
  uploadTrackFileToS3: (...args: Array<any>) => any
  finalizeTrackUpload: (...args: Array<any>) => any
  format: string
  trackUploader: any
  isAdding: boolean
  connectDropTarget: (...args: Array<any>) => any
}
type State = {
  transcode: boolean
}

class UploadTrackFile extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      transcode: true,
    }
  }

  _handleFileDrop = files => {
    handleUploadingFiles(this.props, files)
  }
  _handleDone = () => {
    const { upload_policy_v2_url } = this.props.track
    const { transcode } = this.state
    this.props.finalizeTrackUpload(upload_policy_v2_url, transcode)
    this.props.onDone()
  }
  _handleRequestClose = () => {
    // ignore it for now
    // better way: show confirmation dialog
  }

  _renderTempItems() {
    const item = this.props.trackUploader.tempItem

    if (this.props.trackUploader.tempItem) {
      const { title, artist, album, progress } = item
      const progressIndicator =
        progress < 100 ? (
          <div className="ShortlistTrack_Upload">
            <div
              className="ShortlistTrack_UploadProgress"
              style={{
                width: `${100 - progress}%`,
              }}
            />
          </div>
        ) : (
          <PendingStatus message="Processing..." size="small" />
        )
      const tempItemEl = (
        <div className="Shortlist_Item" key={item.tempId}>
          <div className="ShortlistTrack is-temp">
            <div className="ShortlistTrack_Artwork" />
            <div className="ShortlistTrack_Details">
              <div className="ShortlistTrack_Name">
                {title}
                {progress && progress < 100 ? (
                  <span className="ShortlistTrack_NamePercent">
                    {`${progress.toFixed(0)}%`}
                  </span>
                ) : null}
              </div>
              <div className="ShortlistTrack_ArtistAlbum">
                {artist && album ? (
                  <div>
                    {artist}: {album}
                  </div>
                ) : null}
              </div>
              {progressIndicator}
            </div>
          </div>
        </div>
      )
      return <div className="Shortlist_Items">{tempItemEl}</div>
    }
  }

  _renderSuccess() {
    const { isAdding, track, format } = this.props
    return (
      <div>
        <div className="UploadTrackFile_Message UploadTrackFile_Row">
          {isAdding ? (
            <span>
              Nice work! You&#39;ve added a new format to{' '}
              <strong>&#34;{track.name}&#34;</strong>.
            </span>
          ) : (
            <span>
              Nice work! You&#39;ve replaced the <strong>.{format}</strong> of{' '}
              <strong>&#34;{track.name}&#34;</strong>.
            </span>
          )}
        </div>
      </div>
    )
  }

  _renderDone() {
    return (
      <Button
        caption="Done"
        appearance="primary"
        onClick={this._handleDone}
      />
    )
  }

  _renderUpload() {
    const { connectDropTarget } = this.props
    return (
      <div className="UploadTrackFile_Message UploadTrackFile_Row">
        {this._renderTempItems()}
        {connectDropTarget(
          <div>
            <FileSelect
              className="UploadTrackFile_DropArea"
              onChange={this._handleFileDrop}
            >
              Drag and drop file here, or click here to browse
            </FileSelect>
          </div>,
        )}
      </div>
    )
  }

  _formatsCount() {
    return Object.keys(this.props.track.all_files || {}).length
  }

  _hasUploaded() {
    return this.props.trackUploader.uploadedToS3
  }

  _getOptimisedFormat() {
    // any format will go for detecting if it's audio or not
    const someFormat = this.props.format || this.props.existingFormat
    return getFileTypeByExt(someFormat) === AUDIO_FILE ? 'mp3' : 'mp4'
  }

  _shouldShowTranscodeCheckbox() {
    const { filename } = this.props.trackUploader
    const ext = getExtension(filename)
    const formatIsOptimised = !canFormatBeOptimised(ext)
    return this._hasUploaded() && !formatIsOptimised
  }

  _handleTranscodeChange = evt => {
    this.setState({
      transcode: evt.target.checked,
    })
  }

  _renderTranscodeCheckbox() {
    return (
      <label className="UploadTrackFile_TranscodeLabel UploadTrackFile_Row">
        <input
          className="UploadTrackFile_TranscodeCheckbox"
          type="checkbox"
          checked={this.state.transcode}
          onChange={this._handleTranscodeChange}
        />
        Create a new {this._getOptimisedFormat()} copy of this track to replace
        existing {this._getOptimisedFormat()}
      </label>
    )
  }


  render() {
    const { isAdding, track, onClose } = this.props
    return (
      <Dialog.Root open onOpenChange={this._handleRequestClose}>
        <Dialog.Content maxWidth="600px" aria-describedby={undefined}>
          <Theme>
            <Box>
              <Dialog.Title>
                {isAdding
                  ? `Add format for ${track.name}`
                  : `Replace file for ${track.name} (${this.props.format})`}
              </Dialog.Title>
              <Box mt="4">
                {this._hasUploaded()
                  ? this._renderSuccess()
                  : this._renderUpload()}
                {this._shouldShowTranscodeCheckbox()
                  ? this._renderTranscodeCheckbox()
                  : null}
                <Flex gap="3" mt="6" justify="end">
                  {this._hasUploaded() ? null : (
                    <Dialog.Close>
                      <Button
                        caption="Cancel"
                        appearance="subtle"
                        onClick={onClose}
                      />
                    </Dialog.Close>
                  )}
                  {this._hasUploaded() ? this._renderDone() : null}
                </Flex>
              </Box>
            </Box>
          </Theme>
        </Dialog.Content>
      </Dialog.Root>
    )
  }
}

export default DropTarget(
  [NativeTypes.FILE],
  uploadTrackFileTarget,
  collect,
)(UploadTrackFile) as any
